import { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { apiGet, api } from '../Components/functions/Api';
import { setTermsModal, setTermsAccepted as setTermsAcceptedAction } from '../ReduxSlices/ModalSlice';
import { toast } from 'react-toastify';
import { TERMS_VERSION, TERMS_HASH } from '../utils/termsContent';

/**
 * Build the structured message that the user signs.
 */
export const buildTermsMessage = (walletAddress, timestamp) => {
  return `I accept the CodeDestate Terms of Service.

Terms Version: ${TERMS_VERSION}
Terms Hash: ${TERMS_HASH}
Wallet: ${walletAddress}
Timestamp: ${timestamp}

By signing this message, I acknowledge that I have read, understood,
and agree to be bound by the CodeDestate Terms of Service available
at https://codedestate.com/terms`;
};

/**
 * Sign with EVM wallet (MetaMask, Bybit, Thirdweb) via EIP-191 personal_sign
 */
const signWithEvm = async (message) => {
  const { getSelectedProvider } = await import('../utils/eip6963');
  const evmProvider = getSelectedProvider() || window.ethereum;
  if (!evmProvider) throw new Error("No EVM provider found");
  const { ethers } = await import('ethers');
  const provider = new ethers.BrowserProvider(evmProvider);
  const signer = await provider.getSigner();
  const signerAddress = await signer.getAddress();
  console.log("[signWithEvm] signer address:", signerAddress);
  console.log("[signWithEvm] message:", message);
  const signature = await signer.signMessage(message);
  console.log("[signWithEvm] signature:", signature);
  // Verify locally before sending to backend
  const recovered = ethers.verifyMessage(message, signature);
  console.log("[signWithEvm] recovered address:", recovered);
  console.log("[signWithEvm] match:", recovered.toLowerCase() === signerAddress.toLowerCase());
  return { signature, signatureType: "eip191" };
};

/**
 * Sign with Cosmos wallet (Keplr) via ADR-36 signArbitrary
 */
const signWithCosmos = async (walletType, walletAddress, message) => {
  const chainId = process.env.REACT_APP_NIBIRU_CHAIN_ID || "cataclysm-1";
  let walletProvider;

  if (walletType === 'keplr') {
    walletProvider = window.keplr;
  }

  if (!walletProvider) {
    throw new Error(`${walletType} wallet not available`);
  }

  // signArbitrary creates an ADR-36 amino signature
  const signResponse = await walletProvider.signArbitrary(chainId, walletAddress, message);

  // The signature is base64 encoded
  return {
    signature: signResponse.signature,
    signatureType: "amino",
    pubKey: signResponse.pub_key,
  };
};

/**
 * Hook that manages terms acceptance state and signing flow.
 */
export const useTermsAcceptance = () => {
  const dispatch = useDispatch();
  const account = useSelector((state) => state.auth.account);
  const wallet = useSelector((state) => state.auth.wallet);
  const isConnected = useSelector((state) => state.auth.isConnected);
  const termsModalOpen = useSelector((state) => state.modal.termsModal);
  const termsAccepted = useSelector((state) => state.modal.termsAccepted);

  const setTermsAccepted = useCallback((val) => dispatch(setTermsAcceptedAction(val)), [dispatch]);
  const [checking, setChecking] = useState(false);
  const [signing, setSigning] = useState(false);

  const isEvm = wallet === 'metamask' || wallet === 'bybitWallet' || wallet === 'thirdweb' || wallet === 'web3auth' || wallet === 'rabby' || wallet === 'coinbase' || wallet === 'trust' || wallet === 'okx';
  const isCosmos = wallet === 'keplr';

  /**
   * Check if user has already accepted terms.
   * 1. Check localStorage first (instant)
   * 2. If not cached, query backend
   */
  const checkTermsAcceptance = useCallback(async () => {
    if (!account) return false;

    // Check localStorage first
    const cacheKey = `terms_accepted_${account.toLowerCase()}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { version } = JSON.parse(cached);
      if (version === TERMS_VERSION) {
        setTermsAccepted(true);
        return true;
      }
    }

    // Query backend
    setChecking(true);
    try {
      const result = await apiGet(`user/check-terms/${encodeURIComponent(account)}`);
      if (result.accepted && result.version === TERMS_VERSION) {
        // Cache it
        localStorage.setItem(cacheKey, JSON.stringify({
          version: result.version,
          signedAt: result.signedAt,
        }));
        setTermsAccepted(true);
        setChecking(false);
        return true;
      }
    } catch (error) {
      console.error("Failed to check terms:", error);
    }

    setChecking(false);
    setTermsAccepted(false);
    return false;
  }, [account]);

  /**
   * Check terms when wallet connects
   */
  useEffect(() => {
    if (isConnected && account) {
      checkTermsAcceptance().then((accepted) => {
        if (!accepted) {
          dispatch(setTermsModal(true));
        }
      });
    } else {
      setTermsAccepted(false);
    }
  }, [isConnected, account, checkTermsAcceptance, dispatch]);

  /**
   * Sign and submit terms acceptance
   */
  const signAndAcceptTerms = useCallback(async () => {
    if (!account || !wallet) {
      toast.error("Please connect your wallet first");
      return false;
    }

    setSigning(true);
    try {
      const timestamp = new Date().toISOString();
      const message = buildTermsMessage(account, timestamp);

      let signatureData;

      if (isEvm) {
        signatureData = await signWithEvm(message);
      } else if (isCosmos) {
        signatureData = await signWithCosmos(wallet, account, message);
      } else {
        throw new Error("Unsupported wallet type");
      }

      // Submit to backend
      const result = await api("user/accept-terms", {
        walletAddress: account,
        signature: typeof signatureData.signature === 'object'
          ? JSON.stringify(signatureData.signature)
          : signatureData.signature,
        termsVersion: TERMS_VERSION,
        termsHash: TERMS_HASH,
        timestamp,
        message,
        signatureType: signatureData.signatureType,
      });

      if (result.success) {
        // Cache acceptance
        const cacheKey = `terms_accepted_${account.toLowerCase()}`;
        localStorage.setItem(cacheKey, JSON.stringify({
          version: TERMS_VERSION,
          signedAt: timestamp,
        }));
        setTermsAccepted(true);
        dispatch(setTermsModal(false));
        toast.success("Terms of Service accepted");
        return true;
      } else {
        toast.error(result.message || "Failed to accept terms");
        return false;
      }
    } catch (error) {
      console.error("Terms signing error:", error);
      if (error.message?.includes("rejected") || error.message?.includes("denied") || error.code === 4001) {
        toast.error("Signature request was rejected");
      } else {
        toast.error("Failed to sign terms: " + (error.message || "Unknown error"));
      }
      return false;
    } finally {
      setSigning(false);
    }
  }, [account, wallet, isEvm, isCosmos, dispatch]);

  /**
   * Show terms modal (for defensive gate in TradingForm)
   */
  const requireTermsAcceptance = useCallback(() => {
    if (termsAccepted) return true;
    dispatch(setTermsModal(true));
    return false;
  }, [termsAccepted, dispatch]);

  return {
    termsAccepted,
    termsModalOpen,
    checking,
    signing,
    signAndAcceptTerms,
    checkTermsAcceptance,
    requireTermsAcceptance,
  };
};
